import type { PopoverOrigin } from '@mui/material'

/**
 * Shared placement for every menu that hangs off a SIDEBAR ROW — Help, the profile
 * chip, the reader status. All three open to the RIGHT of the drawer, bottom-aligned
 * with their row, clearing the sidebar by a true 12px gap.
 *
 * Extracted because the same reasoning had been written three times and had already
 * drifted: Help derived the offset from named constants, the profile chip hardcoded
 * `ml: 3` in a comment, and the reader menu opened ABOVE the row centred over the
 * drawer — half on top of the sidebar it belongs to. One source, so a fourth sidebar
 * menu cannot invent a fourth placement (working-conventions §36).
 *
 * Applied in BOTH sidebar states, not just collapsed: one placement is easier to
 * predict than two that swap on width, and at 80px nothing fits inside the drawer
 * anyway. Mirrors Vergence's `side="right" align="end" sideOffset={12}`.
 */

/**
 * A sidebar row's own horizontal padding, in px — `pl: 3` / `pr: 3` on the 4px theme
 * base. Named because the gap below is derived from it, so the two cannot drift apart
 * silently.
 */
const TRIGGER_INLINE_PADDING = 12

/**
 * The visible gap between a popover and whatever it hangs off — the drawer edge for
 * the sidebar menus, the parent paper for a submenu.
 *
 * ONE number for every popover in this corner of the app, which is the arrangement
 * this file started with. It was briefly split: 12 to the sidebar, 4 to a submenu, on
 * the argument that a child should sit closer to its parent than two strangers do.
 * Sound in principle, wrong in practice — side by side the 4 read as cramped and the
 * 12 read as detached, so the split made BOTH edges worse rather than one better. The
 * fix was the value, not the structure: 8 is the middle both were reaching for.
 */
const MENU_SIDE_OFFSET = 8

/**
 * The menu anchors to the row's button, whose right edge stops
 * `TRIGGER_INLINE_PADDING` short of the drawer's right edge. Add that back, then the
 * gap, so the menu clears the sidebar by exactly the offset — rather than a
 * hand-tuned number that happens to look right today.
 */
export const SIDEBAR_MENU_GAP = TRIGGER_INLINE_PADDING + MENU_SIDE_OFFSET

/** Anchor on the ROW's right edge... */
export const SIDEBAR_MENU_ANCHOR_ORIGIN: PopoverOrigin = { horizontal: 'right', vertical: 'bottom' }

/** ...meeting the MENU's left edge, bottoms aligned. */
export const SIDEBAR_MENU_TRANSFORM_ORIGIN: PopoverOrigin = { horizontal: 'left', vertical: 'bottom' }

/**
 * For menus anchored to a ROW BUTTON, which is inset from the drawer edge by the
 * row's padding — Help, the reader status.
 *
 * `mt: 0` cancels `MENU_PAPER_SX`'s 8px drop, which only makes sense for a menu opening
 * DOWNWARD from its trigger; this one opens sideways. It only wins if it is spread AFTER
 * `MENU_PAPER_SX` — check the order at any new call site.
 */
export const SIDEBAR_MENU_PAPER_SX = { mt: 0, ml: `${SIDEBAR_MENU_GAP}px` } as const

/**
 * For menus anchored to an element that is already FLUSH with the drawer edge — the
 * profile chip, whose anchor box is `minWidth: 100%`. There is no row padding to add back, but
 * there IS the drawer's 1px border: the anchor spans the content box, so it stops 1px short of
 * the edge the eye measures from.
 *
 * That 1px was the whole bug. The profile menu sat 11px off the sidebar while its own Theme
 * submenu sat 12px off it, and side by side the two gaps read as uneven — correctly, because they
 * were. Every variant here now adds back exactly what it sits inside: row padding, paper inset,
 * or this border. Which is why these stay separate named constants rather than one number someone
 * later "fixes" into agreement.
 */
/**
 * The drawer's own right border, in px (`MuiDrawer-paper` `border-right: 1px`).
 *
 * Load-bearing for the flush variant and nothing else. An anchor with `minWidth: 100%` spans the
 * drawer's CONTENT box, so its right edge lands 1px inside the drawer's visible right edge — and
 * a bare 12px offset from it therefore renders an 11px gap. Measured: drawer right 80, anchor
 * right 79, menu left 91.
 */
const DRAWER_BORDER_WIDTH = 1

export const SIDEBAR_MENU_PAPER_SX_FLUSH = {
  mt: 0,
  ml: `${DRAWER_BORDER_WIDTH + MENU_SIDE_OFFSET}px`,
} as const

/**
 * How far a ROW INSIDE a menu paper sits from that paper's outer edge, in px. Three
 * layers, none of them guessed:
 *
 *   1px  the paper's own border          (`MuiMenu.paper` in `@repo/ui` theme)
 *   4px  the paper's inset               (`MENU_PAPER_SX` `pr: '4px'`, absolute — §47)
 *   4px  the MenuList's inset            (`MuiMenu.list` `padding: '4px'`)
 *
 * Was 15 (8px paper + 6px list). Both were halved to narrow the content-gap difference between
 * adjacent papers — see the note in `MENU_PAPER_SX`. If either padding changes again, this
 * number must change with it, which is why it is derived in a comment rather than guessed.
 *
 * Exactly the role `TRIGGER_INLINE_PADDING` plays for a sidebar row: the thing a
 * neighbouring popover must add back before the gap. It also explains the two
 * hand-tuned numbers this replaced — `ml: 4` (16px) measured a 1px gap because 15 of
 * those 16px went on the inset, and `ml: 7` (28px) measured ~13px for the same reason.
 */
const MENU_ROW_INLINE_INSET = 9

/**
 * For a SUBMENU flying out of a row inside one of the menus above — the profile menu's
 * Theme submenu. Same `MENU_SIDE_OFFSET` as everything else here; only the
 * compensation differs (a row inside a paper, not a row inside the drawer).
 */
export const SIDEBAR_SUBMENU_PAPER_SX = {
  mt: 0,
  ml: `${MENU_ROW_INLINE_INSET + MENU_SIDE_OFFSET}px`,
} as const

/**
 * The companion to everything above — `useChromeSettledPosition` — lives in
 * `hooks/useChromeSettledPosition.ts`, not here.
 *
 * Every offset in this file is measured from the drawer's right edge, and that edge
 * moves; the hook is what stops a menu being positioned against a width that is still
 * animating. It sits in `hooks/` because `ActionDropdown` needs it too, and a generic
 * input component importing from `components/navigation/Sidebar/` would invert the
 * layering for the sake of one hook.
 */
